import { formatTenThousandths, parseTenThousandths } from '@shared/utils/moneyArithmetic';
import type {
    FundAccountVat,
    StatementBlockKey,
    UnsplitFigures,
    VatRateFigures,
    VatRatePercent,
} from '../types/dealerFundStatementPeriod.types';
import type { PostingSplit } from '../types/fundAccountPostings.types';
import { sameAmount } from '../utils/vatFigures';
import { payloadReaders } from './statementPayloadReaders';

export const VAT_RATE_PERCENTS: readonly VatRatePercent[] = ['0.0000', '5.0000', '20.0000'];
export const STATEMENT_BLOCK_KEYS: readonly StatementBlockKey[] = [
    'motorplan_handled',
    'dealer_handled',
    'internal',
    'paid_out',
];

const MAX_RATES = 3;
const ZERO = '0.00';

export type LineVat = FundAccountVat;

export interface StatementVat {
    openingBalanceNet: string;
    closingBalanceNet: string;
    unsplit: UnsplitFigures;
    balanceUnsplit: UnsplitFigures;
}

export const vatPayloadReaders = (label: string) => {
    const { malformed, readRecord, readMoney, readInt, readArray } = payloadReaders(label);

    const units = (amount: string): bigint => {
        const parsed = parseTenThousandths(amount);
        if (parsed === null) throw malformed('an amount is not a decimal');
        return parsed;
    };

    const total = (amounts: readonly string[]): bigint => amounts.reduce((sum, amount) => sum + units(amount), 0n);

    const readRatePercent = (value: unknown, name: string): VatRatePercent => {
        const found = VAT_RATE_PERCENTS.find((percent) => percent === value);
        if (found === undefined) throw malformed(`${name} vat_rate_percent is not an allowed rate`);
        return found;
    };

    const readRate = (value: unknown, name: string): VatRateFigures => {
        const raw = readRecord(value, name);
        const figures: VatRateFigures = {
            vatRatePercent: readRatePercent(raw.vat_rate_percent, name),
            gross: readMoney(raw.gross, `${name} gross`),
            vat: readMoney(raw.vat, `${name} vat`),
            net: readMoney(raw.net, `${name} net`),
        };
        if (units(figures.gross) !== units(figures.vat) + units(figures.net)) {
            throw malformed(`${name} gross is not vat plus net`);
        }
        if (figures.vatRatePercent === VAT_RATE_PERCENTS[0] && units(figures.vat) !== 0n) {
            throw malformed(`${name} has VAT at 0%`);
        }
        return figures;
    };

    const readVatRates = (value: unknown, name: string, min: number): VatRateFigures[] => {
        const list = readArray(value, name);
        if (list.length < min) throw malformed(`${name} has fewer than ${min} entries`);
        if (list.length > MAX_RATES) throw malformed(`${name} has more than ${MAX_RATES} entries`);
        const rates = list.map((entry) => readRate(entry, `${name} entry`));
        rates.forEach((rate, index) => {
            const position = VAT_RATE_PERCENTS.indexOf(rate.vatRatePercent);
            if (index > 0 && position <= VAT_RATE_PERCENTS.indexOf(rates[index - 1].vatRatePercent)) {
                throw malformed(`${name} is not strictly ascending by rate`);
            }
        });
        return rates;
    };

    const readUnsplit = (value: unknown, name: string): UnsplitFigures => {
        const raw = readRecord(value, name);
        const figures = { postings: readInt(raw.postings, `${name} postings`, 0), gross: readMoney(raw.gross, `${name} gross`) };
        if (figures.postings === 0 && units(figures.gross) !== 0n) throw malformed(`${name} has gross without postings`);
        return figures;
    };

    const assertTies = (
        amount: string,
        figures: LineVat,
        postings: number,
        name: string,
    ): void => {
        const { vat, net, vatRates, unsplit } = figures;
        if (total(vatRates.map((rate) => rate.gross)) + units(unsplit.gross) !== units(amount)) {
            throw malformed(`${name} rates and unsplit gross do not add up to the amount`);
        }
        if (total(vatRates.map((rate) => rate.vat)) !== units(vat)) throw malformed(`${name} rate VAT does not add up to vat`);
        if (total(vatRates.map((rate) => rate.net)) !== units(net)) throw malformed(`${name} rate net does not add up to net`);
        if (vatRates.length === 0 && postings > unsplit.postings) {
            throw malformed(`${name} has recorded postings but no rates`);
        }
    };

    const readLineVat = (raw: Record<string, unknown>, amount: string, postings: number, name: string): LineVat => {
        const figures: LineVat = {
            vat: readMoney(raw.vat, `${name} vat`),
            net: readMoney(raw.net, `${name} net`),
            vatRates: readVatRates(raw.vat_rates, `${name} vat_rates`, 0),
            unsplit: readUnsplit(raw.unsplit, `${name} unsplit`),
        };
        assertTies(amount, figures, postings, name);
        return figures;
    };

    const readRecordedSplit = (raw: Record<string, unknown>, amount: string): PostingSplit => {
        const figures: LineVat = {
            vat: readMoney(raw.vat, 'row vat'),
            net: readMoney(raw.net, 'row net'),
            vatRates: readVatRates(raw.legs, 'row legs', 1),
            unsplit: { postings: 0, gross: ZERO },
        };
        assertTies(amount, figures, 1, 'row');
        return { splitState: 'recorded', vat: figures.vat, net: figures.net, legs: figures.vatRates };
    };

    const readUnsplitSplit = (raw: Record<string, unknown>): PostingSplit => {
        if (!Array.isArray(raw.legs) || raw.legs.length !== 0) throw malformed('an unsplit row has legs');
        if (raw.vat !== null || raw.net !== null) throw malformed('an unsplit row has vat or net');
        return { splitState: 'unsplit', vat: null, net: null, legs: [] };
    };

    const readPostingSplit = (raw: Record<string, unknown>, amount: string): PostingSplit => {
        if (raw.split_state === 'recorded') return readRecordedSplit(raw, amount);
        if (raw.split_state === 'unsplit') return readUnsplitSplit(raw);
        throw malformed('row split_state is not recorded or unsplit');
    };

    const readBlock = (value: unknown): StatementBlockKey => {
        const found = STATEMENT_BLOCK_KEYS.find((key) => key === value);
        if (found === undefined) throw malformed('block is not one of the four statement blocks');
        return found;
    };

    const readRowCount = (value: unknown, rows: readonly { legs: readonly unknown[] }[]): number => {
        const count = readInt(value, 'row_count', 0);
        const expected = rows.reduce((sum, row) => sum + Math.max(1, row.legs.length), 0);
        if (count !== expected) throw malformed('row_count does not match the rows and their legs');
        return count;
    };

    const readStatementVat = (raw: Record<string, unknown>, lines: readonly { unsplit: UnsplitFigures }[]): StatementVat => {
        const figures: StatementVat = {
            openingBalanceNet: readMoney(raw.opening_balance_net, 'opening_balance_net'),
            closingBalanceNet: readMoney(raw.closing_balance_net, 'closing_balance_net'),
            unsplit: readUnsplit(raw.unsplit, 'unsplit'),
            balanceUnsplit: readUnsplit(raw.balance_unsplit, 'balance_unsplit'),
        };
        const postings = lines.reduce((sum, line) => sum + line.unsplit.postings, 0);
        const gross = formatTenThousandths(total(lines.map((line) => line.unsplit.gross)));
        if (figures.unsplit.postings !== postings || !sameAmount(figures.unsplit.gross, gross)) {
            throw malformed('unsplit is not the sum of the lines');
        }
        if (figures.balanceUnsplit.postings < figures.unsplit.postings) {
            throw malformed('balance_unsplit has fewer postings than unsplit');
        }
        return figures;
    };

    return { readVatRates, readUnsplit, readLineVat, readPostingSplit, readBlock, readRowCount, readStatementVat };
};
